import React from 'react'
import { brandColors } from '@vacationtracker/shared/constants/ThemeSetting'
import './LoadingIndicator.less'

export type LoadingIndicatorSize = 'small' | 'medium' | 'large'
export type LoadingIndicatorMode = 'standalone' | 'overlay'

export interface LoadingIndicatorProps {
  size?: LoadingIndicatorSize
  /** `'overlay'` wraps `children`, dims/blurs them, and centers the spinner on top - replaces antd Spin's `spinning={loading}>{children}</Spin>` wrap pattern. */
  mode?: LoadingIndicatorMode
  /** Only relevant in `mode="overlay"`. `false` renders `children` as-is with no spinner. */
  loading?: boolean
  /** Rare escape hatch for a fully custom spinner node (2 real uses found in the audit). */
  indicator?: React.ReactNode
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

const SIZE_PX: Record<LoadingIndicatorSize, number> = {
  small: 16,
  medium: 24,
  large: 32,
}

const BORDER_WIDTH: Record<LoadingIndicatorSize, number> = {
  small: 2,
  medium: 3,
  large: 4,
}

function Spinner({ size, indicator }: { size: LoadingIndicatorSize; indicator?: React.ReactNode }) {
  if (indicator) return <>{indicator}</>

  const px = SIZE_PX[size]
  return (
    <span
      className="vt-loading-ring"
      style={{
        width: px,
        height: px,
        borderWidth: BORDER_WIDTH[size],
        borderColor: brandColors.primaryBg,
        borderTopColor: brandColors.primary,
      }}
    />
  )
}

/**
 * Vacation Tracker's single loading indicator. Matches the look of the existing
 * `CircularProgress` component (77 uses, the app's most-reused custom component - see
 * docs/component-audit/progress-loading.html) rather than antd's default Spin icon, and
 * adds the size and overlay-wrap capabilities Spin had that CircularProgress lacked.
 */
export function LoadingIndicator({ size = 'medium', mode = 'standalone', loading = true, indicator, children, className, style }: LoadingIndicatorProps) {
  if (mode === 'overlay') {
    if (!loading) return <>{children}</>

    return (
      <div className={['vt-loading-overlay-wrap', className].filter(Boolean).join(' ')} style={style}>
        <div className="vt-loading-overlay-content">{children}</div>
        <div className="vt-loading-overlay-mask">
          <Spinner size={size} indicator={indicator} />
        </div>
      </div>
    )
  }

  return (
    <span className={className} style={style}>
      <Spinner size={size} indicator={indicator} />
    </span>
  )
}

export default LoadingIndicator
